import React, { useMemo } from 'react';
import { Box } from '@mui/material';

/** Track half-width from the GM (meters). */
export const TJP_TRACK_LIMIT = 2.4;
/** Link lengths from the GM (meters). pole2 = lower (cart), pole1 = upper. */
const L_LOWER = 0.75;
const L_UPPER = 0.5;

/**
 * Classic cart-pole side view: track, cart, two hinged links.
 * Angles are absolute from vertical (0 = upright), matching GM state.
 */
export default function TwoJointPoleScene({
    cart_position = 0,
    pole1_angle = 0,
    pole2_angle = 0,
    width = 480,
    height = 300,
}) {
    const geo = useMemo(() => {
        const vbW = 480;
        const vbH = 300;
        const trackY = 232;
        const usableHalf = 190;
        const scale = 95; // px per meter

        const cartX = vbW / 2 + (Number(cart_position) / TJP_TRACK_LIMIT) * usableHalf;
        const cartY = trackY;
        const pivotX = cartX;
        const pivotY = cartY - 18;

        // Lower link (pole2) hinged on cart; upper link (pole1) hinged on tip of lower.
        const θ2 = Number(pole2_angle) || 0;
        const θ1 = Number(pole1_angle) || 0;
        const j1x = pivotX + Math.sin(θ2) * L_LOWER * scale;
        const j1y = pivotY - Math.cos(θ2) * L_LOWER * scale;
        const tipX = j1x + Math.sin(θ1) * L_UPPER * scale;
        const tipY = j1y - Math.cos(θ1) * L_UPPER * scale;

        return { vbW, vbH, trackY, cartX, cartY, pivotX, pivotY, j1x, j1y, tipX, tipY };
    }, [cart_position, pole1_angle, pole2_angle]);

    const { vbW, vbH, trackY, cartX, cartY, pivotX, pivotY, j1x, j1y, tipX, tipY } = geo;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <svg
                viewBox={`0 0 ${vbW} ${vbH}`}
                width={width}
                height={height}
                style={{ maxWidth: '100%', display: 'block', borderRadius: 12 }}
                role="img"
                aria-label="Two-joint pole cart visualization"
            >
                <defs>
                    <linearGradient id="tjpSky" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0c1a24" />
                        <stop offset="55%" stopColor="#122830" />
                        <stop offset="100%" stopColor="#1a3338" />
                    </linearGradient>
                    <linearGradient id="tjpFloor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1e3a3f" />
                        <stop offset="100%" stopColor="#0a1214" />
                    </linearGradient>
                    <linearGradient id="tjpCart" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5a6a72" />
                        <stop offset="100%" stopColor="#2c363c" />
                    </linearGradient>
                    <filter id="tjpShadow" x="-40%" y="-40%" width="180%" height="180%">
                        <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.45" />
                    </filter>
                </defs>

                <rect width={vbW} height={vbH} fill="url(#tjpSky)" />

                {/* Floor / ground plane */}
                <rect x="0" y={trackY + 8} width={vbW} height={vbH - trackY - 8} fill="url(#tjpFloor)" />

                {/* Track rail */}
                <rect x="36" y={trackY - 2} width={vbW - 72} height="6" rx="2" fill="#3d555c" />
                <rect x="36" y={trackY} width={vbW - 72} height="3" fill="#6ee7d8" opacity="0.35" />
                {/* Track end stops */}
                <rect x="28" y={trackY - 10} width="10" height="22" rx="2" fill="#4ECDC4" opacity="0.7" />
                <rect x={vbW - 38} y={trackY - 10} width="10" height="22" rx="2" fill="#4ECDC4" opacity="0.7" />

                {/* Cart shadow */}
                <ellipse cx={cartX} cy={trackY + 10} rx="42" ry="6" fill="#000" opacity="0.35" />

                {/* Cart body */}
                <g filter="url(#tjpShadow)">
                    <rect x={cartX - 36} y={cartY - 22} width="72" height="22" rx="4" fill="url(#tjpCart)" stroke="#8aa0a8" strokeWidth="1.5" />
                    {/* Wheels */}
                    <circle cx={cartX - 22} cy={cartY + 2} r="7" fill="#1a1a1a" stroke="#4ECDC4" strokeWidth="1.5" />
                    <circle cx={cartX + 22} cy={cartY + 2} r="7" fill="#1a1a1a" stroke="#4ECDC4" strokeWidth="1.5" />
                    <circle cx={cartX - 22} cy={cartY + 2} r="2.5" fill="#6ee7d8" />
                    <circle cx={cartX + 22} cy={cartY + 2} r="2.5" fill="#6ee7d8" />
                </g>

                {/* Lower link (pole2) */}
                <line
                    x1={pivotX} y1={pivotY} x2={j1x} y2={j1y}
                    stroke="#2cbb5d"
                    strokeWidth="8"
                    strokeLinecap="round"
                />
                <line
                    x1={pivotX} y1={pivotY} x2={j1x} y2={j1y}
                    stroke="#7dffa6"
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity="0.55"
                />

                {/* Upper link (pole1) */}
                <line
                    x1={j1x} y1={j1y} x2={tipX} y2={tipY}
                    stroke="#e8b84a"
                    strokeWidth="7"
                    strokeLinecap="round"
                />
                <line
                    x1={j1x} y1={j1y} x2={tipX} y2={tipY}
                    stroke="#ffe6a0"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    opacity="0.55"
                />

                {/* Joints */}
                <circle cx={pivotX} cy={pivotY} r="7" fill="#0a1214" stroke="#4ECDC4" strokeWidth="2.5" />
                <circle cx={j1x} cy={j1y} r="6" fill="#0a1214" stroke="#2cbb5d" strokeWidth="2.5" />
                <circle cx={tipX} cy={tipY} r="5" fill="#0a1214" stroke="#e8b84a" strokeWidth="2" />

                {/* Soft tip bob for mass feel */}
                <circle cx={tipX} cy={tipY} r="9" fill="#e8b84a" opacity="0.25" />
            </svg>
        </Box>
    );
}
